import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Stars } from "@/components/Badges";
import { toast } from "sonner";
import { LayoutGrid, Calendar, FileText, User, TrendingUp, Sparkles, DollarSign, Eye, Star, CheckCircle2, XCircle } from "lucide-react";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "jobs", label: "Jobs & Quotes", icon: Calendar },
  { key: "profile", label: "My Profile", icon: User },
  { key: "grow", label: "Grow", icon: TrendingUp },
];

const STATUS_COLOR = {
  pending: "bg-gold-light text-gold-dark", scheduled: "bg-lawn-light text-lawn-dark",
  completed: "bg-lawn text-white", canceled: "bg-red-100 text-red-600",
};

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/me/provider").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/me/provider/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/me/bookings").then((r) => setBookings(r.data.bookings)).catch(() => {});
    api.get("/me/quotes").then((r) => setQuotes(r.data.quotes)).catch(() => {});
    api.get("/me/provider/onboarding").then((r) => setOnboarding(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const updateStatus = async (bookingId, status) => {
    try { await api.put(`/bookings/${bookingId}/status`, { status }); toast.success(`Booking ${status}`); load(); }
    catch { toast.error("Failed"); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/me/provider", {
        business_name: profile.business_name, description: profile.description,
        city: profile.city, state: profile.state, phone: profile.phone,
        response_time_hours: Number(profile.response_time_hours), instant_booking: profile.instant_booking,
        service_areas: typeof profile.service_areas === "string" ? profile.service_areas.split(",").map((s) => s.trim()) : profile.service_areas,
      });
      toast.success("Profile saved!"); load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const buyPlacement = async (planKey) => {
    try {
      const co = await api.post("/payments/checkout", { kind: "placement", plan_key: planKey, origin_url: window.location.origin });
      window.location.href = co.data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (!profile) return <DashboardShell title="Provider" nav={NAV} active={active} onNav={setActive}><div className="animate-pulse h-40 bg-white rounded-2xl" /></DashboardShell>;

  return (
    <DashboardShell title="Provider Dashboard" nav={NAV} active={active} onNav={setActive} testId="provider-dashboard">
      {active === "overview" && (
        <div className="space-y-6">
          {profile.status === "pending" && (
            <div className="bg-gold-light border border-gold/30 rounded-2xl p-4 text-gold-dark text-sm font-medium">⏳ Your profile is pending admin approval. It will appear in search once approved.</div>
          )}

          {onboarding && onboarding.percent < 100 && (
            <div className="bg-white border border-border rounded-2xl p-6" data-testid="onboarding-checklist">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-display font-bold text-lawn-ink flex items-center gap-2"><Sparkles className="w-5 h-5 text-lawn" /> Get your business live</h3>
                <span className="text-sm font-bold text-lawn">{onboarding.percent}% complete</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">Complete these steps — especially <span className="font-semibold text-lawn-ink">photos & pricing</span> — to attract more customers.</p>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-4"><div className="h-full bg-lawn transition-all" style={{ width: `${onboarding.percent}%` }} /></div>
              <div className="space-y-2">
                {onboarding.steps.map((s) => (
                  <button key={s.key} data-testid={`onboard-${s.key}`} onClick={() => setActive(s.tab)} disabled={s.done}
                    className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded-xl transition-colors ${s.done ? "opacity-60" : "hover:bg-muted"}`}>
                    {s.done ? <CheckCircle2 className="w-5 h-5 text-lawn shrink-0" /> : <div className="w-5 h-5 rounded-full border-2 border-gold shrink-0" />}
                    <span className={`text-sm ${s.done ? "line-through text-muted-foreground" : "font-medium text-lawn-ink"}`}>{s.label}</span>
                    {!s.done && <span className="ml-auto text-xs text-lawn font-semibold">Add →</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Total earnings", value: `$${analytics?.total_earnings ?? 0}`, icon: DollarSign },
              { label: "Profile views", value: analytics?.profile_views ?? 0, icon: Eye },
              { label: "Completed jobs", value: analytics?.completed_bookings ?? 0, icon: CheckCircle2 },
              { label: "Rating", value: (analytics?.rating ?? 0).toFixed(1), icon: Star },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-5">
                <s.icon className="w-5 h-5 text-lawn mb-2" />
                <p className="font-display text-2xl font-extrabold text-lawn-ink">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-border rounded-2xl p-6">
              <h3 className="font-display font-bold text-lawn-ink mb-4">Recent bookings</h3>
              {bookings.slice(0, 5).map((b) => (
                <div key={b.booking_id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                  <div><p className="text-sm font-semibold text-lawn-ink">{b.service_name}</p><p className="text-xs text-muted-foreground">{b.customer_name}</p></div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLOR[b.status] || "bg-muted"}`}>{b.status}</span>
                </div>
              ))}
              {bookings.length === 0 && <p className="text-sm text-muted-foreground">No bookings yet.</p>}
            </div>
            <div className="bg-lawn-ink rounded-2xl p-6 text-white">
              <Sparkles className="w-6 h-6 text-gold mb-3" />
              <h3 className="font-display font-bold">Your tier: <span className="capitalize text-gold">{profile.tier}</span></h3>
              <p className="text-white/60 text-sm mt-2">Conversion rate: {analytics?.conversion_rate ?? 0}%</p>
              <Button data-testid="upgrade-btn" onClick={() => setActive("grow")} className="w-full mt-4 rounded-full bg-lawn hover:bg-lawn-dark font-semibold">Grow My Business</Button>
            </div>
          </div>
        </div>
      )}

      {active === "jobs" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-display text-lg font-bold text-lawn-ink mb-3">Bookings</h3>
            <div className="space-y-3">
              {bookings.length === 0 && <p className="text-sm text-muted-foreground">No bookings yet.</p>}
              {bookings.map((b) => (
                <div key={b.booking_id} data-testid={`prov-booking-${b.booking_id}`} className="bg-white border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-lawn-ink">{b.service_name} — ${b.total_price}</p>
                    <p className="text-sm text-muted-foreground">{b.customer_name} · {b.address}, {b.zip_code} · {new Date(b.scheduled_date).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLOR[b.status] || "bg-muted"}`}>{b.status}</span>
                    {b.status === "pending" && <><Button data-testid={`accept-${b.booking_id}`} size="sm" className="rounded-full bg-lawn hover:bg-lawn-dark h-8" onClick={() => updateStatus(b.booking_id, "accepted")}>Accept</Button><Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => updateStatus(b.booking_id, "declined")}>Decline</Button></>}
                    {["accepted", "scheduled"].includes(b.status) && <Button data-testid={`complete-${b.booking_id}`} size="sm" className="rounded-full bg-lawn hover:bg-lawn-dark h-8" onClick={() => updateStatus(b.booking_id, "completed")}>Mark Complete</Button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-lawn-ink mb-3">Quote requests</h3>
            <div className="space-y-3">
              {quotes.length === 0 && <p className="text-sm text-muted-foreground">No quote requests.</p>}
              {quotes.map((q) => <QuoteRow key={q.quote_id} q={q} reload={load} />)}
            </div>
          </div>
        </div>
      )}

      {active === "profile" && (
        <div className="bg-white border border-border rounded-2xl p-6 max-w-2xl space-y-4">
          <h3 className="font-display text-lg font-bold text-lawn-ink">Business profile</h3>
          <div><Label>Business name</Label><Input data-testid="prof-name" value={profile.business_name || ""} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} className="mt-1.5 rounded-xl" /></div>
          <div><Label>Description</Label><Textarea data-testid="prof-desc" value={profile.description || ""} onChange={(e) => setProfile({ ...profile, description: e.target.value })} rows={4} className="mt-1.5 rounded-xl" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>City</Label><Input data-testid="prof-city" value={profile.city || ""} onChange={(e) => setProfile({ ...profile, city: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div><Label>State</Label><Input value={profile.state || ""} onChange={(e) => setProfile({ ...profile, state: e.target.value })} className="mt-1.5 rounded-xl" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Phone</Label><Input value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div><Label>Response time (hrs)</Label><Input type="number" value={profile.response_time_hours || ""} onChange={(e) => setProfile({ ...profile, response_time_hours: e.target.value })} className="mt-1.5 rounded-xl" /></div>
          </div>
          <div><Label>Service areas (ZIP codes, comma-separated)</Label><Input data-testid="prof-zips" value={Array.isArray(profile.service_areas) ? profile.service_areas.join(", ") : profile.service_areas} onChange={(e) => setProfile({ ...profile, service_areas: e.target.value })} className="mt-1.5 rounded-xl" /></div>
          <div className="flex items-center justify-between border border-border rounded-xl p-4">
            <div><p className="font-semibold text-sm text-lawn-ink">Instant booking</p><p className="text-xs text-muted-foreground">Let customers book without approval</p></div>
            <Switch data-testid="prof-instant" checked={profile.instant_booking} onCheckedChange={(v) => setProfile({ ...profile, instant_booking: v })} />
          </div>
          <Button data-testid="prof-save" onClick={saveProfile} disabled={saving} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold">{saving ? "Saving…" : "Save Profile"}</Button>
        </div>
      )}

      {active === "grow" && (
        <div className="space-y-6">
          <div className="bg-white border border-border rounded-2xl p-6">
            <h3 className="font-display text-lg font-bold text-lawn-ink">Upgrade your plan</h3>
            <p className="text-sm text-muted-foreground mt-1">You're currently on the <span className="font-semibold capitalize text-lawn">{profile.tier}</span> plan.</p>
            <Button data-testid="grow-pricing-btn" onClick={() => navigate("/pricing")} className="mt-4 rounded-full bg-lawn hover:bg-lawn-dark font-semibold">View Plans & Upgrade</Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-gold-light rounded-2xl p-6">
              <Sparkles className="w-7 h-7 text-gold-dark mb-2" />
              <h4 className="font-display font-bold text-lawn-ink">Sponsored Placement</h4>
              <p className="text-sm text-lawn-ink/70 mt-1">Jump to the top of search for 30 days.</p>
              <p className="font-display text-2xl font-extrabold text-lawn-ink mt-2">$49</p>
              <Button data-testid="buy-sponsored" onClick={() => buyPlacement("sponsored")} className="mt-3 rounded-full bg-gold hover:bg-gold-dark text-white font-semibold">Boost Now</Button>
            </div>
            <div className="bg-lawn-light rounded-2xl p-6">
              <Star className="w-7 h-7 text-lawn-dark mb-2" />
              <h4 className="font-display font-bold text-lawn-ink">Featured Provider</h4>
              <p className="text-sm text-lawn-ink/70 mt-1">Featured badge + top placement for 30 days.</p>
              <p className="font-display text-2xl font-extrabold text-lawn-ink mt-2">$99</p>
              <Button data-testid="buy-featured" onClick={() => buyPlacement("featured")} className="mt-3 rounded-full bg-lawn hover:bg-lawn-dark font-semibold">Get Featured</Button>
            </div>
          </div>
          <div className="bg-lawn-ink rounded-2xl p-6 text-white">
            <h4 className="font-display font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-gold" /> AI Success Coach tips</h4>
            <ul className="mt-3 space-y-2 text-sm text-white/70">
              <li>• Add before/after photos to boost bookings by up to 40%.</li>
              <li>• Respond to quotes within 2 hours to earn the Fast Response badge.</li>
              <li>• Offer recurring plans to increase customer lifetime value.</li>
              <li>• Ask happy customers for reviews to earn the Top Rated badge.</li>
            </ul>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function QuoteRow({ q, reload }) {
  const [price, setPrice] = useState("");
  const [open, setOpen] = useState(false);
  const respond = async () => {
    try { await api.put(`/quotes/${q.quote_id}/respond`, { quote_price: Number(price), message: "" }); toast.success("Quote sent!"); setOpen(false); reload(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="bg-white border border-border rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div><p className="font-semibold text-lawn-ink">{q.service_name}</p><p className="text-sm text-muted-foreground">{q.customer_name} · {q.yard_size}</p></div>
        {q.quote_price ? <span className="font-display font-extrabold text-lawn">${q.quote_price}</span> : (
          open ? (
            <div className="flex gap-2"><Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="$" className="w-24 rounded-xl h-9" /><Button data-testid={`quote-send-${q.quote_id}`} size="sm" className="rounded-full bg-lawn hover:bg-lawn-dark h-9" onClick={respond}>Send</Button></div>
          ) : <Button data-testid={`quote-respond-${q.quote_id}`} size="sm" variant="outline" className="rounded-full" onClick={() => setOpen(true)}>Send Quote</Button>
        )}
      </div>
    </div>
  );
}
